const step = (title, math) => ({ title, math });
const fraction = (a, b) => {
  const sign = a * b < 0 ? "-" : "";
  let n = Math.abs(a), d = Math.abs(b), x = n, y = d;
  while (y) [x, y] = [y, x % y];
  n /= x; d /= x;
  return d === 1 ? `${sign}${n}` : String.raw`${sign}\frac{${n}}{${d}}`;
};

// Steps for catalogue sources. The three older generators already render steps.
export function getLinearInequalitySolutionSteps(task) {
  const v = task.data?.values;
  if (!v) return null;
  switch (task.type) {
    case "MaturaBasic_smallest-integer-linear-inequality": {
      const [a,b,d,c,e] = v, n = c*d-a*e, den = b*e;
      const bound = fraction(n,den), result = Math.ceil(n/den);
      return [
        step("Mnożymy obie strony przez dodatni iloczyn mianowników. Znak nierówności pozostaje bez zmian.", String.raw`\frac{${a}+${b}x}{${d}}\ge\frac{${c}}{${e}}\iff ${e}(${a}+${b}x)\ge${c*d}`),
        step("Rozwijamy nawias i odejmujemy stałą po obu stronach.", String.raw`${e*a}+${den}x\ge${c*d}\iff ${den}x\ge${n}`),
        step("Dzielimy przez dodatni współczynnik przy x.", String.raw`x\ge${bound}\quad\Longleftrightarrow\quad x\in[${bound};+\infty)`),
        step("Wybieramy najmniejszą liczbę całkowitą z tego przedziału. Poprzednia liczba całkowita już do niego nie należy.", String.raw`${result-1}<${bound}\le${result},\qquad x_{\min}=${result}`),
      ];
    }
    case "MaturaBasic_FractionOfDecreasingLinearNumeratorNonStrictInterval": {
      const [a,b,d,c] = v, bound = fraction(a*b-d*c,a);
      return [
        step("Usuwamy mianownik, mnożąc obie strony przez dodatnią liczbę. Znak nierówności się nie zmienia.", String.raw`\frac{${a}(${b}-x)}{${d}}\le${c}\iff ${a}(${b}-x)\le${d*c}`),
        step("Rozwijamy nawias i przenosimy stałą na prawą stronę.", String.raw`${a*b}-${a}x\le${d*c}\iff -${a}x\le${d*c-a*b}`),
        step("Dzielimy przez ujemny współczynnik, dlatego odwracamy znak nierówności.", String.raw`x\ge${bound}`),
        step("Zapisujemy przedział. Domknięty koniec obejmuje wartość graniczną.", String.raw`x\in[${bound};+\infty)`),
      ];
    }
    case "MaturaBasic_PositiveIntegerCountFractionalLinearInequality": {
      const [a,b,d,e] = v, n = e*b+d, den = e*a;
      const bound = fraction(n,den), count = Math.ceil(n/den)-1;
      return [
        step("Mnożymy przez dodatni iloczyn mianowników, zachowując znak nierówności.", String.raw`\frac{${a}x-${b}}{${d}}<\frac1{${e}}\iff ${e}(${a}x-${b})<${d}`),
        step("Rozwijamy nawias, dodajemy stałą i dzielimy przez dodatni współczynnik.", String.raw`${den}x-${e*b}<${d}\iff ${den}x<${n}\iff x<${bound}`),
        step("Uwzględniamy, że liczymy tylko dodatnie liczby całkowite. Ostry znak wyklucza samą granicę.", String.raw`x\in\mathbb Z,\qquad 0<x<${bound}`),
        step("Wypisujemy dopuszczalne liczby i je zliczamy. Następna liczba nie spełnia nierówności.", String.raw`x\in\{${Array.from({length:count},(_,i)=>i+1).join(";")}\},\qquad ${count}<${bound}\le${count+1},\qquad N=${count}`),
      ];
    }
    case "MaturaBasic_FractionalLinearInequalityChooseInterval": {
      const [a,d,c,e] = v, k = e*(a-d), n = d*(e-c), bound = fraction(n,k);
      return [
        step("Mnożymy przez dodatni iloczyn mianowników. Znak nierówności się nie zmienia.", String.raw`1-\frac{${a}}{${d}}x<\frac{${c}}{${e}}-x\iff ${d*e}-${a*e}x<${c*d}-${d*e}x`),
        step("Przenosimy wyrazy z x na prawą stronę, a stałe na lewą.", String.raw`${d*e}-${c*d}<(${a*e}-${d*e})x\iff ${n}<${k}x`),
        step("Dzielimy przez dodatnią liczbę i zapisujemy nierówność z x po lewej stronie.", String.raw`${bound}<x\iff x>${bound}`),
        step("Wybieramy otwarty przedział: ostry znak wyklucza wartość graniczną.", String.raw`x\in(${bound};+\infty)`),
      ];
    }
    case "MaturaBasic_NegativeLinearInequalityFractionRight": {
      const [a,b,c,d] = v, k = 1-a*d, n = c+a*b*d, bound = fraction(n,k);
      return [
        step("Mnożymy przez dodatni mianownik i rozwijamy nawias.", String.raw`-${a}(x+${b})\le\frac{${c}-x}{${d}}\iff -${a*d}x-${a*b*d}\le${c}-x`),
        step("Dodajemy x i stałą do obu stron, następnie redukujemy wyrazy podobne.", String.raw`(${1}-${a*d})x\le${c}+${a*b*d}\iff ${k}x\le${n}`),
        step("Współczynnik przy x jest ujemny. Przy dzieleniu odwracamy znak nierówności.", String.raw`x\ge${bound}`),
        step("Zapisujemy przedział z domkniętym lewym końcem, ponieważ równość jest dopuszczona.", String.raw`x\in[${bound};+\infty)`),
      ];
    }
    case "MaturaBasic_LinearFractionInequalityGreatestInteger": {
      const [u,offset,a,b] = v, n = (u+offset)*a*b, den = a+b, bound = fraction(n,den), result = Math.floor(n/den);
      return [
        step("Mnożymy obie strony przez dodatni iloczyn mianowników, bez zmiany znaku nierówności.", String.raw`${u}-\frac{x}{${a}}\ge\frac{x}{${b}}-${offset}\iff ${u*a*b}-${b}x\ge${a}x-${offset*a*b}`),
        step("Zbieramy stałe po lewej stronie, a wyrazy z x po prawej.", String.raw`${u*a*b}+${offset*a*b}\ge(${a}+${b})x\iff ${n}\ge${den}x`),
        step("Dzielimy przez dodatni współczynnik i zapisujemy rozwiązanie.", String.raw`x\le${bound}\quad\Longleftrightarrow\quad x\in(-\infty;${bound}]`),
        step("Wybieramy największą dopuszczalną liczbę całkowitą. Jeśli granica jest całkowita, także należy do rozwiązania.", String.raw`${result}\le${bound}<${result+1},\qquad x_{\max}=${result}`),
      ];
    }
    default: return null;
  }
}
